import { createSlice } from '@reduxjs/toolkit';

// Checks if response was successful.
// If `status` is not present, 2xx is implied.
function isSuccess(payload) {
  const { status } = payload;
  return !(status && status >= 400);
}

// Loads the `data` and `errors` from the API response `payload` into the given `state` part of the store.
// Supporting records in `data` (if any) will also get loaded as-is.
function loadRecord(state, payload) {
  const { data, errors } = payload;
  state.errors = errors;
  return Object.assign(state, data);
}

// Stores the `recordData` into the given `keyedRecords` object (keyed by `recordData.id`).
// Will replace an existing entry (if any) keyed under the same `id`.
function setById(keyedRecords, recordData) {
  keyedRecords[recordData.id] = recordData;
  return keyedRecords;
}

// Reducer factories for read & write actions.
function readPendingFn(stateKey) {
  return (state) => {
    state[stateKey] = { isLoading: true };
  };
}
function readFulfilledFn(stateKey, loadRecordFn) {
  return (state, { payload }) => {
    const record = loadRecordFn(state[stateKey], payload);
    record.isLoading = false;
  };
}
function writePendingFn(stateKey) {
  return (state) => {
    state[stateKey].isSaving = true;
  };
}
function writeFulfilledFn(stateKey, loadRecordFn, setRecordFn) {
  return (state, { payload }) => {
    const record = loadRecordFn(state[stateKey], payload);
    record.isSaving = false;
    // call function (if given) to mutate the collection on success
    if (setRecordFn && isSuccess(payload)) setRecordFn(state, record);
  };
}

// Checks for reuse of action types (which is not allowed).
// Logs an error and returns `false` if reuse is detected, or `true` otherwise.
// NOTE:
// Reuse of action types is not allowed as it would be ambiguous which operation type was intended.
// For example, if the `show` and `update` operations both used the `editRecord` action, and the action is dispatched,
// there is no clear way to exactly determine whether `show` or `update` was the intended operation.
function assertActionNotReused(reducers, actionType, opType) {
  if (!actionType) {
    // sanity check
    console.error(
      '[createCrudSlice] Action type for `pending` or `fulfilled` is missing:',
      { opType }
    );
    return false;
  }
  if (reducers[actionType]) {
    console.error('[createCrudSlice] Reuse of action types is not allowed:', {
      actionType,
      opType,
    });
    return false;
  }
  return true;
}

// Creates a store slice based on standardized CRUD conventions.
// Argument structure:
// {
//   /* passed directly as `name` to `createSlice` */
//   name: 'someRecords',
//   /* key name holding the array of record data items returned by the API */
//   collectionName: 'some_records',
//   /* key name holding the record data returned by the API */
//   recordName: 'some_record',
//   /* actions configuration (each entry is optional, but at least one is needed) */
//   actionsConfig: {
//     /* the `index` operation is special as the response is assumed to be a collection */
//     index: {
//       /* each operation specifies the `read` or `write` async action (or both) */
//       /* NOTE: an async action is assumed to have `pending` and `fulfilled` sub-types */
//       /* IMPORTANT: action objects cannot be shared across operations */
//       read: indexSomeRecords,
//     },
//     /* all other operations (besides `index`) are assumed to be on individual records */
//     create: {
//       read: newSomeRecord,
//       write: createSomeRecord,
//       /* by default, all "standard" operations (on success) will update the collection */
//       /* this behaviour can be disabled via `noIndex: true` */
//       noIndex: true,
//     },
//     update: {
//       read: editSomeRecord,
//       write: updateSomeRecord,
//     },
//     /* `destroy` is special case as it will remove records from the collection */
//     destroy: {
//       read: SomeRecord,
//       write: destroySomeRecord,
//       /* the removal behaviour can also be disabled via `noIndex: true` */
//       noIndex: true,
//     },
//     /* more "standard" operations can be defined */
//     [other]: {},
//     ...
//   },
// }
//
export function createCrudSlice({
  name,
  collectionName,
  recordName,
  actionsConfig,
}) {
  // Loads the `data` and `errors` from the API response `payload` into the given `records` collection of the store,
  // with the record data collection (as `collectionName`) converted to a keyed object (by `id`) before storing.
  // Supporting records in `data` (if any) will also get loaded as-is.
  function loadRecords(records, payload) {
    const { data } = payload;
    if (data) {
      // convert Array to Object keyed by ID
      data[collectionName] = data[collectionName]?.reduce(setById, {}) || {};
    }
    return loadRecord(records, payload);
  }

  // Stores the given `record` into the `records` collection of the store.
  function setRecord(state, record) {
    const keyedRecords = state.records[collectionName];
    const recordData = record[recordName];
    return setById(keyedRecords, recordData);
  }

  // Removes the given `record` from the `records` collection of the store.
  function removeRecord(state, record) {
    const keyedRecords = state.records[collectionName];
    const recordData = record[recordName];
    delete keyedRecords[recordData.id];
  }

  // config options for `createSlice`
  const initialState = {};
  const extraReducers = {};

  // configure the `initialState` and `extraReducers` based on the given `actionsConfig`
  for (const opType in actionsConfig) {
    const { read, write, noIndex } = actionsConfig[opType];
    const isCollection = opType === 'index';
    const stateKey = isCollection ? `records` : `record_for_${opType}`;
    const loadRecordFn = isCollection ? loadRecords : loadRecord;

    initialState[stateKey] = {};

    if (read) {
      if (assertActionNotReused(extraReducers, read.pending, opType)) {
        extraReducers[read.pending] = readPendingFn(stateKey);
      }
      if (assertActionNotReused(extraReducers, read.fulfilled, opType)) {
        extraReducers[read.fulfilled] = readFulfilledFn(stateKey, loadRecordFn);
      }
    }
    if (write) {
      if (assertActionNotReused(extraReducers, write.pending, opType)) {
        extraReducers[write.pending] = writePendingFn(stateKey);
      }
      if (assertActionNotReused(extraReducers, write.fulfilled, opType)) {
        // determine the `setRecordFn` to use for mutating the collection after a single record add/update/remove
        // skipped if:
        // - this is a collection-level (e.g. `index`) operation (since mutation is already handled by `loadRecords`)
        // - the operation is marked as `noIndex`
        // - the `actionsConfig` does not have a collection-level operation (i.e. there is no collection to mutate)
        const setRecordFn =
          isCollection || noIndex || !actionsConfig.index
            ? null
            : opType === 'destroy'
            ? removeRecord
            : setRecord;
        extraReducers[write.fulfilled] = writeFulfilledFn(
          stateKey,
          loadRecordFn,
          setRecordFn
        );
      }
    }
  }

  return createSlice({
    name,
    initialState,
    reducers: {},
    extraReducers,
  });
}

// Creates the selectors for the store slice created via `createCrudSlice`.
// Just use the same options passed into `createCrudSlice`, e.g.
//   const options = {...};
//   const { reducer } = createCrudSlice(options);
//   const { index, create, update } = createCrudSliceSelectors(options);
//
export function createCrudSliceSelectors({ name, actionsConfig }) {
  const selectors = {};
  for (const opType in actionsConfig) {
    const stateKey = opType === 'index' ? `records` : `record_for_${opType}`;
    selectors[opType] = (state) => state?.[name]?.[stateKey];
  }
  return selectors;
}

//////////////////////////////////
// NOTES ON ASSUMED CONVENTIONS //
//////////////////////////////////

// Expected CRUD-based API response structure:
//
// payload = {
//   data: {
//     /* for APIs that return a collection of records (e.g. `index`) */
//     [collectionName]: [
//       { /* `recordData` */
//         id: 123, /* the record ID is expected to be present and named `id` */
//         [fieldName1]: 'some value',
//         [fieldName2]: 'some value',
//         ...
//       }
//     ],
//     /* for APIs that return a singular record (e.g. `show`) */
//     [recordName]: { /* `recordData` */
//       id: 123,
//       [fieldName1]: 'some value',
//       [fieldName2]: 'some value',
//       ...
//     },
//     /* for APIs that return additional supporting records */
//     [supportingRecords1]: [...],
//     [supportingRecords2]: [...],
//     ...
//   },
//   errors: { /* nested `errors` structure (can be an empty `[]` if there are no errors) */
//     [recordName]: {
//       [fieldName1]: [msg1, msg2, ...],
//       [fieldName2]: [msg1, msg2, ...],
//       ...
//     }
//   }
//   status, /* HTTP status code (if not present, 2xx is implied) */
// }

// Store structure used by `createCrudSlice`:
//
// state = {
//   /* collection of records obtained via the `index` action, may be augmented by mutative actions */
//   records: {
//     [collectionName]: { /* `keyedRecords` */
//       123: { /* each `recordData` (structure kept as-is from the API response) is keyed by ID */
//         id: 123,
//         [fieldName1]: 'some value',
//         [fieldName2]: 'some value',
//         ...
//       },
//       ...
//     },
//     /* supporting records (structure kept as-is from the API response, if available) */
//     [supportingRecords1]: [...],
//     [supportingRecords2]: [...],
//     ...,
//     /* `errors` (structure kept as-is from the API response) */
//     errors: {},
//     /* indicates if the collection is loading */
//     isLoading: false,
//   },
//   /* used by the respective mutative actions (e.g. `create`/`update`/`delete`) */
//   [`record_for_${action}`]: {
//     /* `recordData` (structure kept as-is from the API response, if available) */
//     [recordName]: {}
//     /* supporting records (structure kept as-is from the API response, if available) */
//     [supportingRecords1]: [...],
//     [supportingRecords2]: [...],
//     ...,
//     /* `errors` (structure kept as-is from the API response) */
//     errors: {},
//     /* indicates if the record is loading */
//     isLoading: false,
//     /* indicates if the record is saving */
//     isSaving: false,
//   },
//   ...
// }
